import { List, ListItem } from '@material-ui/core';
import { PRODUCTS, ProductType } from 'cart-model';
import { EventType } from 'common-event';
import { Fund, UnitCategoryCode } from 'funds-model';
import React, { FunctionComponent, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VisibilityWrapper from '../../VisibilityWrapper/VisibilityWrapper';
import { FundListItemWithPotentialProfit, ROI, Skeleton } from 'ui-components';
import { logAddToCart, logFundEvent } from '../../../model/events';
import { FundEventsContextProvider } from '../../../model/FundEventsContext';
import { urlToDataWizard } from '../../../model/integration';
import ProductWizardState from '../../../model/ProductWizardState';
import { productWizardActions } from '../../../model/store';
import FundDetailsButtonWithDialog from '../../FundDetailsButtonWithDialog/FundDetailsButtonWithDialog';
import { FundSmallChart } from '../../FundSmallChart/FundSmallChart';
import Screen from '../../ProductWizard/Screen';
import PotentialProfitSection from './PotentialProfitSection';

import useStyles from './FundList.style';

const FundList: FunctionComponent<{
  funds: Array<Fund>;
  initialAmount: number;
  monthlyAmount: number;
  productType: ProductType;
  isEntrepreneur?: boolean;
  rateKey?: ROI;
}> = ({ funds, initialAmount, monthlyAmount, productType, isEntrepreneur, rateKey = ROI.M12 }) => {
  const SELECT_RECOMMENDED_FUNDS_SCREEN = Screen.SELECT_RECOMMENDED_FUNDS;
  const c = useStyles({});
  const dispatch = useDispatch();
  const config = useSelector((state: ProductWizardState) => state.config);
  const unitCategoryCode = PRODUCTS[productType].unitCategory;

  const handleSelectFund = useCallback(
    (fund: Fund) => {
      const cartFund = {
        fund,
        productType,
        amount: initialAmount,
        screenCode: SELECT_RECOMMENDED_FUNDS_SCREEN.code,
      };
      dispatch(productWizardActions.clearCartFunds());
      dispatch(productWizardActions.addCartFund(cartFund));
      dispatch(productWizardActions.setCartFundAmount(cartFund));
      logFundEvent(EventType.productAddToCart, SELECT_RECOMMENDED_FUNDS_SCREEN, fund, productType);
      logAddToCart(fund, productType, SELECT_RECOMMENDED_FUNDS_SCREEN, cartFund.amount);
      window.location.href = config.dataWizardEnabled ? '/data-wizard' : urlToDataWizard();
    },
    [dispatch, productType, initialAmount, config, SELECT_RECOMMENDED_FUNDS_SCREEN]
  );

  return (
    (!funds || funds.length === 0) ? (
      <List className={c.list}>
        {['skeleton-1', 'skeleton-2', 'skeleton-3'].map((id) => (
          <ListItem key={id} className={c.listItem}>
            <div style={{ width: '100%' }}>
              <Skeleton width="100%" height={308} />
            </div>
          </ListItem>
        ))}
      </List>
    ) :
    <List className={c.list}>
      <FundEventsContextProvider screen={SELECT_RECOMMENDED_FUNDS_SCREEN}>
        {funds.map((fund, index) => (
          <FundListEntry
            key={fund.id}
            fund={fund}
            index={index}
            className={c.listItem}
            unitCategoryCode={unitCategoryCode}
            productType={productType}
            initialAmount={initialAmount}
            monthlyAmount={monthlyAmount}
            isEntrepreneur={isEntrepreneur}
            onSelectFund={handleSelectFund}
            screen={SELECT_RECOMMENDED_FUNDS_SCREEN}
            rateKey={rateKey}
          />
        ))}
      </FundEventsContextProvider>
    </List>
  );
};

const FundListEntry: FunctionComponent<{
  fund: Fund;
  index: number;
  className: string;
  unitCategoryCode: UnitCategoryCode;
  productType: ProductType;
  initialAmount: number;
  monthlyAmount: number;
  isEntrepreneur?: boolean;
  onSelectFund: (fund: Fund) => void;
  screen: typeof Screen.SELECT_RECOMMENDED_FUNDS;
  rateKey: ROI;
}> = ({
  fund, index, className, unitCategoryCode, productType,
  initialAmount, monthlyAmount, isEntrepreneur, onSelectFund, screen: SELECT_RECOMMENDED_FUNDS_SCREEN,
  rateKey,
}) => {
  const [wasVisible, setWasVisible] = useState(false);

  const handleVisibilityChange = useCallback((isVisible: boolean) => {
    if (isVisible) setWasVisible(true);
  }, []);

  return (
    <VisibilityWrapper setVisibility={handleVisibilityChange}>
      <ListItem data-testid="table-row" className={className}>
        <FundListItemWithPotentialProfit
          fund={fund}
          isFundInadequate={false}
          roi={rateKey}
          unitCategoryCode={unitCategoryCode}
          index={index}
          onSelectFund={onSelectFund}
          renderFundChart={(fund, unitCategoryCode, chartRateKey) => (
            <FundSmallChart
              fund={fund}
              unitCategory={unitCategoryCode}
              changeRateKey={chartRateKey}
            />
          )}
          renderFundDetailsDialog={(dialogFund, _unitCategoryCode, dialogIndex, options) => (
            <FundDetailsButtonWithDialog
              fund={dialogFund}
              productType={productType}
              buttonLabel={dialogFund.name}
              source={SELECT_RECOMMENDED_FUNDS_SCREEN}
              index={dialogIndex}
              customButton={options.customButton}
              className={options.className}
              titleAsButton={options.titleAsButton}
              changeRateKey={rateKey}
            />
          )}
          renderPotentialProfitContent={wasVisible ? () => (
            <PotentialProfitSection
              fund={fund}
              initialAmount={initialAmount}
              monthlyAmount={monthlyAmount}
              productType={productType}
              isEntrepreneur={isEntrepreneur}
              index={index}
            />
          ) : undefined}
        />
      </ListItem>
    </VisibilityWrapper>
  );
};

export default FundList;
